import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { studentAuthAPI } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { GraduationCap, Lock, ShieldCheck, KeyRound } from 'lucide-react';

const StudentChangePassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isFirstLogin = searchParams.get('first') === '1';

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const student = (() => {
    try {
      return JSON.parse(localStorage.getItem('student') || 'null');
    } catch {
      return null;
    }
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields.');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('The new passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await studentAuthAPI.changePassword(currentPassword, newPassword);
      toast.success('Password updated. Please continue.');
      navigate('/student/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-cyan-50 flex items-center justify-center p-4"
      data-testid="student-change-password-page"
    >
      <div className="w-full max-w-md">
        <Card className="border-slate-200 shadow-xl">
          <CardHeader className="text-center pb-3">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <ShieldCheck className="w-7 h-7 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">
              {isFirstLogin ? 'Set your new password' : 'Change password'}
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              {isFirstLogin
                ? 'For security, please replace the default password with a new one.'
                : 'Update your password. It must be at least 6 characters.'}
            </p>
            {student?.name && (
              <p className="text-xs text-slate-400 mt-2 flex items-center justify-center gap-1">
                <GraduationCap className="w-3 h-3" />
                {student.name} · {student.enrollment_id}
              </p>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">
                  {isFirstLogin ? 'Default password (your phone number)' : 'Current password'}
                </Label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="current_password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="pl-9"
                    autoComplete="current-password"
                    data-testid="current-password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password">New password</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="new_password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-9"
                    autoComplete="new-password"
                    minLength={6}
                    data-testid="new-password-input"
                  />
                </div>
                <p className="text-xs text-slate-500">At least 6 characters. Don't reuse your phone or enrollment number.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm new password</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="confirm_password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-9"
                    autoComplete="new-password"
                    minLength={6}
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="change-password-submit"
              >
                {loading ? 'Updating…' : 'Update password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StudentChangePassword;
